import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Text, Link, Heading } from "@amzn/storm-ui";
import { isUndefined } from "lodash";
import { useCallback, useEffect } from "react";

import { INSIGHT_REFRESH_STATUS_QUIP } from "../../../../config/insights.config";
import { TELEMETRY_ID } from "../../../../constants/telemetry";
import { formatDate, DateFormat } from "../../../../utils/date.utils";
import { formatUploadDate } from "../../../../utils/insightFamilies.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";

interface InsightRefreshWarningProps {
    uploadDate: string;
    plannedRefreshDate?: Date;
    insight: InsightFamily;
}

export const InsightRefreshWarning: React.FC<InsightRefreshWarningProps> = ({
    uploadDate,
    plannedRefreshDate,
    insight,
}) => {
    const { identifier, familyName, insightFamilyTrackingId } = insight;

    useEffect(() => {
        logWithTakt(TELEMETRY_ID.INSIGHT_REFRESH_WARNING_OPEN, {
            identifier,
            familyName,
            insightFamilyTrackingId,
        });
    }, [insight]);

    const handleRefreshStatusClick = useCallback(() => {
        logWithTakt(TELEMETRY_ID.INSIGHT_REFRESH_CLICK, {
            identifier,
            familyName,
            insightFamilyTrackingId,
        });
        window.open(INSIGHT_REFRESH_STATUS_QUIP, "_blank");
    }, [insight]);

    return (
        <>
            <Heading data-testid="refresh-warning-header" renderAs="h4">
                The refresh for this insight is delayed
            </Heading>
            <br />
            <Text data-testid="last-updated-on-text">
                Last updated on: {formatUploadDate(uploadDate)}
            </Text>
            <Text
                data-testid="planned-refresh-date-text"
                hidden={isUndefined(plannedRefreshDate)}
            >
                Planned refresh date:{" "}
                {formatDate(plannedRefreshDate, DateFormat.LONG_DATE_FORMAT)}
            </Text>
            <Text data-testid="refresh-status-text">
                For detailed status and ETA, please visit our refresh status
                page{" "}
                <Link
                    data-testid="refresh-status-link"
                    onClick={handleRefreshStatusClick}
                    external
                >
                    here
                </Link>
            </Text>
        </>
    );
};
